import { cn } from '@repo/ui';
import {
  type FormEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useId,
} from 'react';

/** 틀이 쓰는 모달 연결 — 네이티브 `<dialog>` 의 ref 와 cancel·close 처리(예: useModalDialog 결과가 이 모양이다). */
interface DialogFrameModal {
  readonly ref: RefObject<HTMLDialogElement | null>;
  readonly onCancel: (event: SyntheticEvent<HTMLDialogElement>) => void;
  readonly onClose: (event: SyntheticEvent<HTMLDialogElement>) => void;
}

interface DialogFrameProps {
  /** 모달 연결(useModalDialog 결과 등) — 모달 띄우기·Escape·포커스 복귀는 호출부가 맡고, 이 틀은 모양만 맡는다. */
  readonly modal: DialogFrameModal;
  readonly title: string;
  readonly children: ReactNode;
  /** 아래쪽 버튼 줄 — 주 동작·닫기 등. */
  readonly footer: ReactNode;
  /** 주면 본문과 버튼 줄을 한 폼으로 감싼다 — 버튼 줄의 type="submit" 이 이 폼을 제출한다. */
  readonly onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** 직접 검증하는 폼만 네이티브 required/email 검증을 끈다. 기본은 브라우저 검증을 유지한다. */
  readonly noValidate?: boolean;
  readonly className?: string;
  /**
   * 본문 칸에 덧입힐 클래스(cn 으로 합친다) — 기본 본문은 높이 상한(70dvh) 안에서 스크롤한다. 창 높이를 채우는 본문(디자인 보기 창)은
   * 여기서 상한을 창 높이로 풀고(max-h-full — tailwind-merge 가 max-h-none 은 합치지 못한다) 세로 flex 로 늘린다. 주지 않으면 기본 모양 그대로다.
   */
  readonly bodyClassName?: string;
}

/**
 * 네이티브 모달 `<dialog>` 의 공용 틀 — 에디터 대화상자(EditorDialog)와 같은 각진 흰 판·굵은 윗선·얇은 선으로 나눈
 * 제목 줄과 버튼 줄. preflight 가 지운 가운데 정렬(m-auto)을 되살리고 뒤 배경을 어둡게 깐다.
 */
export const DialogFrame = ({
  modal,
  title,
  children,
  footer,
  onSubmit,
  noValidate = false,
  className,
  bodyClassName,
}: DialogFrameProps): ReactElement => {
  const titleId = useId();
  const content = (
    <>
      <div className={cn('max-h-[70dvh] overflow-y-auto px-6 py-5 text-sm', bodyClassName)}>
        {children}
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-foreground/10 px-6 py-4">
        {footer}
      </div>
    </>
  );

  return (
    <dialog
      ref={modal.ref}
      onCancel={modal.onCancel}
      onClose={modal.onClose}
      aria-modal="true"
      aria-labelledby={titleId}
      className={cn(
        'm-auto w-[calc(100%-2rem)] max-w-md border-t-2 border-foreground bg-background p-0 text-foreground shadow-2xl backdrop:bg-black/50',
        className,
      )}
    >
      <div className="border-b border-foreground/10 px-6 py-4">
        <h2 id={titleId} className="text-lg font-bold tracking-[-0.02em]">
          {title}
        </h2>
      </div>
      {onSubmit === undefined ? (
        content
      ) : (
        <form onSubmit={onSubmit} noValidate={noValidate}>
          {content}
        </form>
      )}
    </dialog>
  );
};
